<template>
  <div class="survey">
    <div class="columns">
      <div class="column">
        <h4 class="title is-4">
          {{ survey.title }}
          <div v-if="survey.is_quiz" class="tag is-primary">
            <span>Тест</span>
          </div>
          <div v-else class="tag is-primary">
            <span>Анонимный опрос</span>
          </div>
          <div v-if="!survey.published" class="tag is-info">
            <span>Не опубликовано</span>
          </div>
          <div v-if="survey.participated" class="tag is-success">
            <span>Отправлено</span>
          </div>
          <div v-if="reviewed" class="tag is-info">
            <span>Оценено</span>
          </div>
        </h4>
      </div>
      <div class="column is-one-third has-text-right" v-if="survey.published">
        <span v-if="$moment().isAfter(survey.end_at) && !surveyInProgress">
          <b-icon icon="clock" size="is-small"></b-icon>
          Закрылось
          {{ $moment.duration($moment(this.survey.end_at).diff()).humanize(true) }}
        </span>
        <template v-else-if="$moment().isSameOrAfter(survey.start_at) && !surveyInProgress">
          <div v-if="duration.current <= duration.target">
            <span :class="{
                'progress-label': !survey.participated
            }">
              <b-icon icon="clock" size="is-small" v-if="survey.participated"></b-icon>
              Закроется через {{ duration.label }}
            </span>
            <progress
              class="progress is-large"
              v-if="!survey.participated"
              :class="{
                'is-success': !dateWarning,
                'is-warning': dateWarning && !dateDanger,
                'is-danger': dateDanger,
              }"
              :value="duration.current"
              :max="duration.target"
            ></progress>
          </div>
        </template>
        <span v-else-if="$moment().isBefore(survey.start_at)">
          <template v-if="this.beforeStartDuration.current > 0">
            <b-icon icon="clock" size="is-small"></b-icon>
            Откроется через {{ beforeStartDuration.label }}
          </template>
        </span>
      </div>
    </div>

    <div class="columns">
        <div class="column">
            <div class="tags">
                <template v-for="scope in scopes">
                  <a class="tag is-primary"
                     v-if="scope.url"
                     :key="scope.item"
                     :href="scope.url">
                     {{ scope.title }}
                  </a>
                  <div class="tag"
                       v-else
                       :key="scope.item">
                    {{ scope.title }}
                  </div>
                </template>
                <template v-if="survey.generate_questions != null">
                  <div
                    class="tag"
                    v-if="survey.generate_questions == 'teacher-rating'">
                    Оценка качества преподавания
                  </div>
                </template>
            </div>
        </div>
        <div class="column is-half has-text-right">
            <p>
                <span>Автор:</span>
                <a :href="survey.author.url" target="_self">
                    {{ survey.author.short_name }}
                    <b-icon icon="open-in-new" size="is-small"></b-icon>
                </a>
            </p>
            <template v-if="survey.shares && survey.shares.length > 0">
                <div class="tags is-right" style="margin: 0">
                    <p class="tag is-transparent is-medium" style="padding: 0">Доступ:</p>
                    <a
                        class="tag is-primary"
                        v-for="user in $_.map(survey.shares, 'user')"
                        :key="user.id"
                        :href="user.url"
                        target="_blank">
                        {{ user.short_name }}
                    </a>
                </div>
            </template>
            <template v-if="survey.published">
                <p>Дата открытия:
    {{ $moment(survey.start_at).local().format("dddd, YYYY-MM-DD HH:mm") }}
                </p>
                <p>Дата закрытия:
    {{ $moment(survey.end_at).local().format("dddd, YYYY-MM-DD HH:mm") }}
                </p>
            </template>
            <template v-if="survey.duration">
                <p>Продолжительность теста:
    {{ $moment.duration(survey.duration, 'minutes').format('h __ m __', {
        trim: 'all'
    }) }}
                </p>
            </template>
        </div>
    </div>

    <p v-if="parsedDescription">
      <span style="white-space: pre-wrap;" v-html="parsedDescription"></span>
    </p>

    <transition name="fade">

        <div class="answers" v-show="questions.length && answersLoaded">
            <answer
                v-for="index in questions.length"
                ref="answers"
                :survey="survey"
                :is-quiz="survey.is_quiz"
                :can-edit="!survey.participated && survey.participates && !sessionExpired"
                v-model="questions[index - 1]"
                @update="updateAnswer"
                :key="index"
            />
        </div>

    </transition>

    <transition name="fade">

        <template v-if="survey.participates && !sessionFinished && !sessionExpired">

        <div class="section has-text-centered">

            <template v-if="survey.is_quiz && !survey.current_session &&
                $moment().isBefore(survey.end_at)">
                <template v-if="survey.active">
                  <h5 class="is-size-5" v-if="survey.duration > 0">
                      Время выполнения теста:
                      <br>
                      <b>{{ survey.duration }}</b> минут
                  </h5>

                  <br>

                  <div class="field" v-if="survey.active">
                      <p class="control has-text-centered">
                          <button
                              @click="beforeStartQuiz"
                              class="button is-primary is-outlined">
                              <span>Начать тестирование</span>
                          </button>
                      </p>
                  </div>
                </template>
                <template v-else>
                  <template v-if="this.beforeStartDuration.current > 0">
                    <h5 class="is-size-5">
                      Тест начнётся через {{ beforeStartDuration.label }}
                    </h5>
                  </template>
                  <template v-else>
                    <h5 class="is-size-5">
                        Тест начинается...
                    </h5>
                    <small>
                        Пожалуйста, <a onclick="location.reload()">перезагрузите страницу</a>, если тест не начался автоматически.
                    </small>
                  </template>
                </template>
            </template>
            <template v-else-if="!survey.is_quiz && !survey.active && questions.length == 0">
                <template v-if="this.beforeStartDuration.current > 0">
                <h5 class="is-size-5">
                    Анкетирование начнётся через {{ beforeStartDuration.label }}
                </h5>
                </template>
                <template v-else>
                <h5 class="is-size-5">
                    Анкетирование начинается...
                </h5>
                <small>
                    Пожалуйста, <a onclick="location.reload()">перезагрузите страницу</a>, если анкетирование не началось автоматически.
                </small>
                </template>
            </template>
            
            <div class="field" v-if="!sessionExpired && answersLoaded">
                <p class="control has-text-centered">
                    <button
                        v-if="questions.length > 0"
                        @click="submit"
                        class="button is-primary is-outlined">
                        <span v-if="survey.is_quiz">Завершить</span>
                        <span v-else>Отправить</span>
                    </button>
                </p>
            </div>
        </div>

        </template>

    </transition>

    <template v-if="survey.participated && answersLoaded">
        <section class="section has-text-centered">
            <h4 class="is-size-4">
                <span v-if="survey.is_quiz">
                    <template v-if="survey.current_session.score > -1">
                        {{ resultString }} баллов ({{ resultPercentage }}%)
                    </template>
                    <template v-else>
                        Ответы отправлены
                    </template>
                </span>
                <span v-else>Спасибо за участие!</span>
            </h4>
        </section>
    </template>

  </div>
</template>

<script>
import anchorme from "anchorme";
import errors from "./mixins/errors";
import toasts from "./mixins/toasts";
import scopeTitle from "./mixins/survey/scopeTitle";
import Answer from "./survey/Answer";

let answerDebounceInterval = 1000;
let currentAnswerPutRequest = null;
let currentAnswerPutPromise = null;
let previousQuestion = null;

export default Vue.extend({
  props: {
    canManage: {
      type: Boolean,
      required: false,
      default: false
    },
    user: {
      type: Object,
      required: true
    },
    survey: {
      type: Object,
      required: true
    }
  },
  mixins: [errors, toasts, scopeTitle],
  components: { Answer },
  data() {
    return {
      questions: [],
      answers: [],
      answersLoaded: false,
      answersUploaded: false,
      beforeStartDuration: {
        label: null
      },
      duration: {
        current: 0,
        target: 0,
        label: null
      },
      sessionDuration: {
        current: 0,
        target: 0,
        label: null
      }
    };
  },
  watch: {
    survey: {
      deep: true,
      handler() {
        this.updateDuration();
      }
    }
  },
  computed: {
    scopes() {
      let result = []

      this.survey.scope.forEach((item, i) => {
        let url = null
        let match = item.match(/^(group) (\d+)$/)

        if (match && match[1] == 'group') {
          url = `/users/groups/${match[2]}`
        }

        result.push({
          url,
          item,
          title: this.survey.scope_titles[i],
        })
      })

      return result
    },
    surveyInProgress() {
      return (
        this.survey.current_session != null &&
        !this.sessionExpired &&
        !this.sessionFinished
      );
    },
    parsedDescription() {
      let content = this.survey.description ? this.survey.description : ''

      return anchorme(content, {
        attributes: [
          {
            name: "target",
            value: "_blank"
          }
        ],
      })
    },
    reviewed() {
      return (
        this.survey.is_quiz &&
        this.survey.participated &&
        _.get(this.survey.current_session, "score", -1) > -1
      );
    },
    resultPercentage() {
      return parseInt(
        100 *
          (this.survey.current_session.score /
            this.survey.current_session.total_score)
      );
    },
    resultString() {
      return `${this.survey.current_session.score} / ${
        this.survey.current_session.total_score
      }`;
    },
    putAnswer() {
      return _.throttle(answer => {
        this.$bugsnag.leaveBreadcrumb('Answer option clicked');

        return axios
          .put(
            `/api/surveys/${this.survey.id}/answers/${answer.question}`,
            answer
          )
          .catch(error => {
            this.report(error);
          })
          .finally(() => {
            currentAnswerPutPromise = null;
          });
      }, answerDebounceInterval);
    },
    canViewSubmitSection() {
      return (
        !this.survey.participated &&
        this.survey.participates &&
        (!this.survey.current_session || this.canSubmit)
      );
    },
    sessionExpired() {
      if (this.survey.duration == null) {
        return false;
      }

      return !(
        !this.survey.is_quiz ||
        (this.survey.is_quiz &&
          (this.survey.current_session == null ||
            _.get(this.survey.current_session, "expired") === false))
      );
    },
    sessionFinished() {
      if (this.survey.is_quiz) {
        return (
          this.survey.participated &&
          (this.survey.current_session != null ||
            _.get(this.survey.current_session, "finished") === true)
        );
      } else {
        return this.survey.participated;
      }
    },
    dateWarning() {
      return false;
    },
    dateDanger() {
      return this.duration.current / this.duration.target >= 0.75;
    }
  },
  mounted() {
    if (
      (!this.survey.is_quiz && this.survey.active) ||
      (this.survey.is_quiz && this.survey.current_session) ||
      this.survey.participated ||
      this.canManage ||
      this.$can('view all quizzes')
    ) {
      this.loadQuestions().then(questions => {
        this.loadAnswers();
      });
    }

    // Animate duration.
    setInterval(_ => {
      this.refreshDuration();
    }, 1000); // Update every second

    this.refreshDuration();
  },
  methods: {
    refresh() {
      return axios
        .get(`/api/surveys/${this.survey.id}`)
        .then(response => {
          this.survey = response.data;
          resolve(this.questions);
        })
        .catch(error => {
          this.toast("error", "@unexpected", error);
          reject();
        });
    },
    refreshDuration() {
      this.updateDuration();

      if (
        this.survey.current_session != null &&
        this.sessionDuration.current >= this.sessionDuration.target
      ) {
        if (this.survey.is_quiz && !this.survey.participated) {
          this.questions = [];

          let loading = this.$loading.open();

          axios
            .get(`/api/surveys/${this.survey.id}/sessions`)
            .then(response => {
              this.survey.current_session = response.data;
            })
            .catch(error => {
              this.toast("error", "@unexpected", error);
            })
            .finally(() => {
              loading.close();
            });

          this.survey.participated = true;
        }
      }

      this.$emit("update-taskbar", {
        duration: this.sessionDuration,
        hidden:
          this.survey.current_session == null ||
          this.survey.current_session.duration == null ||
          this.survey.current_session.delta < 1 ||
          this.survey.current_session.finished ||
          this.survey.current_session.expired,
        session: this.survey.current_session
      });
    },
    beforeStartQuiz() {
      if (this.survey.duration > 0) {
        this.$dialog.confirm({
          title: "Начало тестирования",
          message:
            `Вы собираетесь начать прохождние теста.<br>` +
            `После начала тестирования у Вас будет <b>${
              this.survey.duration
            }</b> минут на его прохождение без возможности поставить на паузу.`,
          confirmText: "Начать тестирование",
          cancelText: "Отмена",
          type: "is-warning",
          hasIcon: true,
          onConfirm: () => {
            this.startQuiz();
          }
        });
      } else {
        this.startQuiz();
      }
    },
    startQuiz() {
      let loading = this.$loading.open();

      this.loadQuestions().then(() => {
        this.$nextTick(() => {
          _.each(this.$refs.answers, answer => {
            answer.update(
              _.find(this.answers, {
                question: answer.model.question
              })
            );
          });
        });

        axios
          .post(`/api/surveys/${this.survey.id}/sessions`)
          .then(response => {
            this.answersLoaded = true;
            this.survey.current_session = response.data;
            this.refreshDuration();
          })
          .catch(error => {
            this.toast("error", "@unexpected", error);
          })
          .finally(() => {
            loading.close();
          });
      });
    },
    loadQuestions() {
      return new Promise((resolve, reject) => {
        if (this.survey.questions) {
          this.questions = this.survey.questions;
          resolve(this.questions);
        } else {
          return axios
            .get(`/api/surveys/${this.survey.id}/questions`)
            .then(response => {
              this.questions = response.data;
              resolve(this.questions);
            })
            .catch(error => {
              this.toast("error", "@unexpected", error);
              reject();
            });
        }
      });
    },
    loadAnswers() {
      return new Promise((resolve, reject) => {
        return axios
          .get(`/api/surveys/${this.survey.id}/answers/${this.user.alias}`)
          .then(response => {
            this.answers = _.get(response.data, "answers", []);
            _.each(this.$refs.answers, answer => {
              answer.update(
                _.find(this.answers, {
                  question: answer.model.question
                })
              );
            });

            this.answersLoaded = true;
            resolve(this.answers);
          })
          .catch(error => {
            // Omit answer load error.
          });
      });
    },
    updateAnswer(answer) {
      let index = _.findIndex(this.answers, {
        uuid: answer.uuid
      });

      if (index > -1) {
        this.answers.splice(index, 1, answer);
      } else {
        this.answers.push(answer);
      }

      if (currentAnswerPutRequest && previousQuestion != answer.question) {
        currentAnswerPutRequest.flush();
      }

      currentAnswerPutRequest = this.putAnswer;
      previousQuestion = answer.question;

      currentAnswerPutPromise = this.putAnswer(answer);
    },
    submit() {
      // There is pending answer update.
      if (currentAnswerPutPromise != null) {
        return;
      }

      let valid = true;
      _.each(this.$refs.answers, answer => {
        valid = valid && answer.validate();
      });

      this.$bugsnag.leaveBreadcrumb('Survey submit initiated');

      if (valid) {
        let answers = [];

        _.each(this.$refs.answers, answer => {
          answers.push(answer.model);
        });

        let loading = this.$loading.open();

        this.$bugsnag.leaveBreadcrumb('Sending answers');

        let finish = () => {
          return axios
            .post(`/api/surveys/${this.survey.id}/finish`, answers)
            .then(response => {
              this.survey.participated = true;
              this.survey.current_session = response.data;
              if (this.survey.is_quiz) {
                this.questions = [];
              }
              this.toast(
                "success",
                this.survey.is_quiz ? "Ответ отправлен" : "Анкета отправлена"
              );
              this.$forceUpdate();
              this.refreshDuration();
            })
            .catch(error => {
              this.toast("error", "@unexpected", error);
            })
            .finally(_ => {
              loading.close();
            });
        };

        if (currentAnswerPutRequest && currentAnswerPutPromise) {
          currentAnswerPutPromise.then(() => {
            finish();
          });
          currentAnswerPutRequest.flush();
        } else {
          finish();
        }
      }
    },
    updateDuration() {
      let now = this.$moment();
      let start = this.$moment(this.survey.start_at);
      let end = this.$moment(this.survey.end_at);
      this.duration = {
        current: this.$moment.duration(now.diff(start)).asSeconds(),
        target: this.$moment.duration(end.diff(start)).asSeconds(),
        label: this.$moment.duration(end.diff(now)).format(
          function() {
            return this.duration.asSeconds() < 60 ? "s __" : "d __ h __ m __";
          },
          {
            trim: "all"
          }
        )
      };

      this.beforeStartDuration = {
        current: Math.floor(this.$moment.duration(start.diff(now)).asSeconds()),
        label: this.$moment.duration(start.diff(now)).format(
          function() {
            return this.duration.asSeconds() < 60 ? "s __" : "d __ h __ m __";
          },
          {
            trim: "all"
          }
        )
      };

      if (
        this.beforeStartDuration.current == 0 &&
        this.survey.active == false
      ) {
        setTimeout(() => location.reload(), 2000);
      }

      if (
        this.survey.current_session &&
        _.get(this.survey.current_session, "finished", false) === false &&
        _.get(this.survey.current_session, "expired", false) === false
      ) {
        let sessionStart = this.$moment(this.survey.current_session.start_at);
        let sessionEnd = this.$moment(this.survey.current_session.end_at);
        this.sessionDuration = {
          delta: this.$moment.duration(sessionEnd.diff(now)).asSeconds(),
          current: this.$moment.duration(now.diff(sessionStart)).asSeconds(),
          target: this.$moment
            .duration(sessionEnd.diff(sessionStart))
            .asSeconds(),
          label: this.$moment.duration(sessionEnd.diff(now)).format(
            function() {
              return this.duration.asSeconds() < 60 ? "s __" : "d __ h __ m __";
            },
            {
              trim: "all"
            }
          )
        };
      }
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/Survey.vue